import { useMemo } from "react";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import { flattenCssVariables } from "@circle-react-shared/BuilderUI/Editor/utils/flattenCssVariables";
import { extractCssVariables } from "@circle-react-shared/BuilderUI/ThemeConfiguration/SiteStyles/Tokens/utils/extractCssVariables";
import { extractCssVariablesFromTheme } from "@circle-react-shared/BuilderUI/ThemeConfiguration/SiteStyles/Tokens/utils/extractCssVariablesFromTheme";
import { generateNextColorKey } from "@circle-react-shared/BuilderUI/ThemeConfiguration/utils/generateNextColorKey";
import { EMAIL_CORE_COLORS } from "../Tokens/Colors/constants";

export const useEmailCustomColors = () => {
  const {
    currentCanvasTheme,
    appearance,
    updateCustomColors,
    onAddCustomColor,
  } = useBuilderStore(state => ({
    currentCanvasTheme: state.currentCanvasTheme,
    appearance: state.appearance,
    updateCustomColors: state.updateCustomColors,
    onAddCustomColor: state.onAddCustomColor,
  }));

  const customColors = useMemo(
    () => currentCanvasTheme?.customColors ?? {},
    [currentCanvasTheme?.customColors],
  );

  const cssVariables = useMemo(
    () => currentCanvasTheme?.cssVariables ?? {},
    [currentCanvasTheme?.cssVariables],
  );

  const flattenedCssVariables = useMemo(
    () => flattenCssVariables(cssVariables, appearance),
    [cssVariables, appearance],
  );

  const handleAddCustomColor = async (color: string) => {
    const newColorKey = generateNextColorKey(customColors);

    try {
      await onAddCustomColor?.({ ...customColors, [newColorKey]: color });
    } catch (error) {
      console.error(error);
    } finally {
      updateCustomColors({ ...customColors, [newColorKey]: color });
    }
  };

  const coreColorsCssVariables = useMemo(
    () => extractCssVariablesFromTheme(EMAIL_CORE_COLORS),
    [],
  );

  const themeColors = useMemo(
    () => extractCssVariables(flattenedCssVariables, coreColorsCssVariables),
    [flattenedCssVariables, coreColorsCssVariables],
  );

  return {
    customColors,
    handleAddCustomColor,
    themeColors,
    flattenedCssVariables,
  };
};
